/**
 * What a list loader answers when one of its requests failed: the store is
 * left as it was (an empty list would read as "no draft"), and the caller
 * can tell. The dispatched action is the answer when every request succeeded.
 */
export const LOAD_FAILED = { success: false as const }

/** A child request's failure, as the list loaders' helpers report it. */
export const failed = (result: unknown): boolean =>
  result === null ||
  result === undefined ||
  (typeof result === 'object' &&
    (result as { success?: unknown }).success === false)

/**
 * Whether every prerequisite read a form or modal waits on succeeded. A read
 * that answered nothing (`undefined`) failed too: a thunk whose catch swallows
 * the error answers exactly that. A read that is not needed is left out of
 * the list, never passed as `undefined`.
 */
export const loadedAll = (results: unknown[]): boolean =>
  results.every(result => !failed(result))

/**
 * Every read settled, a rejection counted as a failure. Some thunks reject
 * instead of answering a failure value (a request with no response makes
 * their catch throw), and `Promise.all` would stop at the first one and
 * leave a form waiting on a loader for ever.
 */
export const settleAll = (reads: unknown[]): Promise<unknown[]> =>
  Promise.allSettled(reads).then(settled =>
    settled.map(result =>
      result.status === 'fulfilled' ? result.value : LOAD_FAILED,
    ),
  )

/** The list a list loader answered with (its dispatched action's payload), or none. */
export const listOf = <T>(result: unknown): T[] => {
  const payload = (result as { payload?: unknown } | null | undefined)?.payload
  return Array.isArray(payload) ? (payload as T[]) : []
}
